import type { ComponentType } from "react"
import { memo, useEffect, useState } from "react"
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers"
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { IconGripDotsVerticalOutline18 as GripIcon } from "nucleo-ui-outline-18"

import type {
	SidebarBadgeStyle,
	SidebarItemId,
	SidebarLayout,
	SidebarVisibility,
} from "@productlane/lib/src/sidebar-layout"
import { cn } from "@productlane/lib/src/browser"
import {
	COMBINED_SIDEBAR_GROUPS,
	combinedGroupOf,
	isSidebarItemId,
	moveCombinedSidebarItem,
	moveSidebarItem,
	setSidebarItemVisibility,
	SIDEBAR_BADGE_STYLES,
	SIDEBAR_ITEMS,
	SIDEBAR_SECTIONS,
	SIDEBAR_VISIBILITIES,
} from "@productlane/lib/src/sidebar-layout"
import { Badge, Dialog, Dot, IconButton, Select } from "@productlane/ui"

import type { DragEndEvent } from "@dnd-kit/core"
import { SIDEBAR_LAYOUT_COPY as copy } from "./copy"

export interface CustomizeSidebarItem {
	title: string
	icon: ComponentType<{ className?: string }>
	iconClassName?: string
}

/** `sections` lists the rail's sections; `combined` mirrors the combined sidebar's two groups. */
export type CustomizeSidebarVariant = "sections" | "combined"

export type CustomizeSidebarItems = Partial<
	Record<SidebarItemId, CustomizeSidebarItem>
>

const CARD_CLASS =
	"rounded-[10px] border border-gray-200 bg-gray-50 dark:border-gray-700/80 dark:bg-gray-850"

const BADGE_STYLE_OPTIONS = SIDEBAR_BADGE_STYLES.map((value) => ({
	value,
	label: copy.badgeStyles[value],
}))

export function visibilityOptions(id: SidebarItemId) {
	return SIDEBAR_VISIBILITIES.filter(
		(value) => value !== "badged" || SIDEBAR_ITEMS[id].badgeable,
	).map((value) => ({ value, label: copy.visibility[value] }))
}

// Ghost trigger that hugs its value, so the hover box wraps the text and chevron only.
const ROW_SELECT_CLASS =
	"h-7 w-auto gap-1.5 bg-transparent pl-2 pr-1.5 shadow-none ring-0 hover:bg-gray-150 data-[state=open]:bg-gray-150 dark:bg-transparent dark:hover:bg-gray-800 dark:data-[state=open]:bg-gray-800"

const BadgePreview = memo(({ style }: { style: SidebarBadgeStyle }) => (
	<span className="flex w-7 flex-none items-center justify-end">
		{style === "count" && (
			<Badge variant="nav-count" className="bg-gray-200/80 dark:bg-gray-800">
				1
			</Badge>
		)}
		{style === "dot" && <Dot size="sm" color="gray" />}
	</span>
))

const SortableRow = memo(
	({
		id,
		item,
		visibility,
		onVisibilityChange,
	}: {
		id: SidebarItemId
		item: CustomizeSidebarItem
		visibility: SidebarVisibility
		onVisibilityChange: (id: SidebarItemId, value: SidebarVisibility) => void
	}) => {
		const {
			attributes,
			listeners,
			setNodeRef,
			setActivatorNodeRef,
			transform,
			transition,
			isDragging,
		} = useSortable({ id })
		const hidden = visibility === "hidden"
		const Icon = item.icon

		return (
			<div
				ref={setNodeRef}
				style={{ transform: CSS.Transform.toString(transform), transition }}
				data-testid="customize-sidebar-row"
				data-item-id={id}
				className={cn(
					"flex h-11 items-center gap-2.5 bg-gray-50 pl-1.5 pr-1.5 dark:bg-gray-850",
					isDragging && "relative z-10 shadow-sm",
				)}
			>
				<IconButton
					ref={setActivatorNodeRef}
					variant="ghost"
					size="md"
					Icon={GripIcon}
					className="touch-none text-tertiary"
					{...attributes}
					{...listeners}
					aria-label={copy.reorder(item.title)}
				/>
				<Icon
					className={cn(
						"h-4 w-4 flex-none text-primary",
						item.iconClassName,
						hidden && "opacity-40",
					)}
				/>
				<span
					className={cn(
						"min-w-0 flex-1 truncate text-sm font-medium",
						hidden ? "text-tertiary" : "text-primary",
					)}
				>
					{item.title}
				</span>
				<Select
					name={`sidebar-visibility-${id}`}
					className="flex-none"
					selectClassName={ROW_SELECT_CLASS}
					popoverAlign="end"
					value={visibility}
					options={visibilityOptions(id)}
					onChange={(value) =>
						onVisibilityChange(id, value as SidebarVisibility)
					}
				/>
			</div>
		)
	},
)

export const CustomizeSidebarPanel = memo(
	({
		items,
		layout,
		onChange,
		variant = "sections",
	}: {
		items: CustomizeSidebarItems
		layout: SidebarLayout
		onChange: (layout: SidebarLayout) => void
		variant?: CustomizeSidebarVariant
	}) => {
		// Holds the new order through the frame before the synced layout catches up, so a dropped row never snaps back.
		const [draft, setDraft] = useState(layout)
		useEffect(() => {
			setDraft(layout)
		}, [layout])

		const sensors = useSensors(
			useSensor(PointerSensor),
			useSensor(KeyboardSensor, {
				coordinateGetter: sortableKeyboardCoordinates,
			}),
		)

		const commit = (next: SidebarLayout) => {
			setDraft(next)
			onChange(next)
		}

		const handleDragEnd = ({ active, over }: DragEndEvent) => {
			if (!over || active.id === over.id) return
			const from = String(active.id)
			const to = String(over.id)
			if (!isSidebarItemId(from) || !isSidebarItemId(to)) return
			const move =
				variant === "combined" ? moveCombinedSidebarItem : moveSidebarItem
			commit(move(draft, from, to))
		}

		const handleVisibilityChange = (
			id: SidebarItemId,
			value: SidebarVisibility,
		) => {
			commit(setSidebarItemVisibility(draft, id, value))
		}

		const groups =
			variant === "combined"
				? COMBINED_SIDEBAR_GROUPS.map((group) => ({
						key: group,
						// The sidebar shows the rows above Workspace without a header, so this does too.
						label: group === "workspace" ? copy.sections.workspace : undefined,
						ids: draft.combined.filter((id) => combinedGroupOf(id) === group),
					}))
				: SIDEBAR_SECTIONS.map((section) => ({
						key: section,
						label: copy.sections[section],
						ids: draft.order[section],
					}))

		return (
			<div className="flex flex-col pb-2">
				<div
					className={cn(CARD_CLASS, "flex h-12 items-center gap-2 pl-4 pr-1.5")}
				>
					<span className="min-w-0 flex-1 truncate text-sm font-medium text-primary">
						{copy.badgeStyleLabel}
					</span>
					<BadgePreview style={draft.badgeStyle} />
					<Select
						name="sidebar-badge-style"
						className="flex-none"
						selectClassName={ROW_SELECT_CLASS}
						popoverAlign="end"
						value={draft.badgeStyle}
						options={BADGE_STYLE_OPTIONS}
						onChange={(value) =>
							commit({ ...draft, badgeStyle: value as SidebarBadgeStyle })
						}
					/>
				</div>

				{groups.map((group) => {
					const ids = group.ids.filter((id) => items[id])
					if (ids.length === 0) return null
					return (
						<div
							key={group.key}
							data-testid={`customize-sidebar-${group.key}`}
							className={cn(!group.label && "pt-5")}
						>
							{group.label && (
								<div className="pb-2 pt-5 text-sm font-medium text-primary">
									{group.label}
								</div>
							)}
							<div className={cn(CARD_CLASS, "overflow-hidden py-1")}>
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									modifiers={[restrictToVerticalAxis, restrictToParentElement]}
									onDragEnd={handleDragEnd}
								>
									<SortableContext
										items={ids}
										strategy={verticalListSortingStrategy}
									>
										{ids.map((id) => {
											const item = items[id]
											if (!item) return null
											return (
												<SortableRow
													key={id}
													id={id}
													item={item}
													visibility={draft.visibility[id]}
													onVisibilityChange={handleVisibilityChange}
												/>
											)
										})}
									</SortableContext>
								</DndContext>
							</div>
						</div>
					)
				})}
			</div>
		)
	},
)

export const CustomizeSidebarDialog = memo(
	({
		isOpen,
		onOpenChange,
		items,
		layout,
		onChange,
		variant,
	}: {
		isOpen: boolean
		onOpenChange: (open: boolean) => void
		items: CustomizeSidebarItems
		layout: SidebarLayout
		onChange: (layout: SidebarLayout) => void
		variant?: CustomizeSidebarVariant
	}) => (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={copy.dialogTitle}
		>
			<CustomizeSidebarPanel
				items={items}
				layout={layout}
				onChange={onChange}
				variant={variant}
			/>
		</Dialog>
	),
)
